import { useState } from 'react';
import { Button, Dropdown, Menu, message, Modal } from 'antd';

import { DebounceSelect } from 'components/common';
import { classnames } from 'lib/classnames';
import useI18n from 'lib/hooks/useI18n';
import { Automation, Scenes } from 'lib/models';
import { escapeMatchesQueryArg } from 'lib/parse';
import { BasicValue } from 'lib/types/automation';
import { AutomationNode, AutomationNodeValue } from 'lib/types/models';

import AutomationScenesModal from './AutomationScenesModal';
import { useSaveAutomation } from './hooks';

import styles from './AutomationDetailHeader.module.less';

const cx = classnames(styles);

interface AddAutomationDropdownProps {
  nodes: AutomationNode<AutomationNodeValue>[];
  basicInfo: BasicValue;
  disabled?: boolean;
}

export const AddAutomationDropdown: React.FC<AddAutomationDropdownProps> = ({ nodes, basicInfo, disabled }) => {
  const [createKey, setCreateKey] = useState<string>();
  const [selectScenes, setSelectScenes] = useState<string>();
  const { t } = useI18n();

  const { saveAutomation } = useSaveAutomation({ nodes, enableAuto: true });
  const fetchScenesOptions = async name => {
    const query = new Parse.Query(Scenes);
    if (name) {
      query.matches('name', escapeMatchesQueryArg(name), 'i');
    }
    const list = await query.select(['name']).find();
    return list.map(item => {
      return {
        value: item.id,
        label: item.get('name'),
      };
    });
  };

  return (
    <>
      <Dropdown
        disabled={disabled}
        dropdownRender={() => (
          <Menu
            className={cx('dropdown-wrapper')}
            onClick={async ({ key }) => {
              const hasAutomation = await new Parse.Query(Automation).equalTo('name', basicInfo.name).first();
              if (hasAutomation) {
                message.error(t('validate.nameNoRepeat'));
                return;
              }
              setCreateKey(key);
            }}
          >
            <Menu.Item key="scenes" className={cx('dropdown-item')}>
              {t('pages.automation.template.existScenes')}
            </Menu.Item>
            <Menu.Item key="create" className={cx('dropdown-item')}>
              {t('pages.automation.template.newScenes')}
            </Menu.Item>
          </Menu>
        )}
      >
        <Button key="open" type="primary">
          {t('pages.automation.template.scenes')}
        </Button>
      </Dropdown>
      <AutomationScenesModal
        visible={createKey === 'create'}
        buttonText={t('pages.automation.template.newScenes')}
        onCancel={async data => {
          if (data) {
            // 创建后场景将自动化保存到新建场景里
            await saveAutomation(basicInfo, data.objectId);
          }
          setCreateKey(undefined);
        }}
      />
      <Modal
        title={t('pages.automation.template.existScenes')}
        open={createKey === 'scenes'}
        onOk={async () => {
          if (!selectScenes) {
            message.error(t('pages.automation.template.message'));
            return;
          }
          await saveAutomation(basicInfo, selectScenes);
          setCreateKey(undefined);
        }}
        onCancel={() => {
          setSelectScenes(null);
          setCreateKey(undefined);
        }}
      >
        <div>{t('pages.automation.template.label')}</div>
        <DebounceSelect
          className={cx('scenes-select')}
          placeholder={t('placeholder.default.select')}
          showSearch
          fetchOptions={fetchScenesOptions}
          value={selectScenes}
          onChange={v => setSelectScenes(v)}
        />
      </Modal>
    </>
  );
};
